import { DirectionsCar, Flag, Visibility } from "@mui/icons-material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import GetAppIcon from "@mui/icons-material/GetApp";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable, MRT_ToggleDensePaddingButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { downloadCsv } from "../../../../utils/filesUtils";
import snackbarUtils from "../../../../utils/snackbarUtils";
import { tableConfig } from "../../../commons/table/config/tableConfig";
import DetailsTable from "../../../commons/table/utilsTable/detailsTableAccidents";
import TitleContainer from "../../../commons/table/utilsTable/titleWithFetchData";
import { StyledAccidentArtTable } from "../../../styled/accident";

const AccidentArtTable = ({
  data,
  loading,
  noRefresh,
  fetchData,
  handleAccidentDetail,
  displayAccidents,
  handleEdit,
  handleOpenLogsDialog,
  handleIngresarTraslado,
  handleVerTrasladosAnteriores,
}) => {
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const roles = useSelector((state) => state.auth.roles);
  const esPerfilDeSoporte = roles?.tipo === "Soporte" || roles?.tipo === "Administrador";

  const columns = useMemo(() => [
    {
      header: "Prestador",
      accessorKey: "prestador",
    },
    {
      header: "CUIT Prestador",
      accessorKey: "cuitPrestador",
      size: 150,
      minSize: 140,
      maxSize: 170,
    },
    {
      header: "Estado",
      accessorKey: "estado",
    },
    {
      header: "Siniestro",
      accessorKey: "siniestro",
      size: 110,
      minSize: 100,
      maxSize: 120,
      Cell: ({ row }) => {
        if (row.original.siniestro === "0") return " ";
        return row.original.siniestro;
      },
    },
    {
      header: "Documento",
      accessorKey: "documento",
      size: 110,
      minSize: 100,
      maxSize: 120,
    },
    {
      header: "Trabajador",
      accessorFn: (row) =>
        `${row.apellidoTrabajador || ""}, ${row.nombreTrabajador || ""}`,
      enableColumnActions: false,

      filterFn: (row, columnId, filterValue) => {
        const fullName = `${row.original.apellidoTrabajador || ""}, ${row.original.nombreTrabajador || ""
          }`.toLowerCase();

        return fullName.includes(filterValue.toLowerCase());
      },
      Cell: ({ row }) =>
        `${row.original.apellidoTrabajador}, ${row.original.nombreTrabajador}`,
    },
    {
      header: "Accidente",
      accessorKey: "fechaHoraAccidente",
      size: 115,
      minSize: 110,
      maxSize: 130,
      filterFn: (row, columnId, filterValue) => {
        const cellValue = moment(row.getValue(columnId)).format("DD/MM/YYYY");
        return cellValue.includes(filterValue);
      },
      Cell: ({ row }) =>
        moment(row.original.fechaHoraAccidente).format("DD/MM/YYYY"),
    },
    {
      header: "Próximo Control",
      accessorKey: "proximoControl",
      filterFn: (row, columnId, filterValue) => {
        const cellValue = moment(row.getValue(columnId)).format("DD/MM/YYYY");
        return cellValue.includes(filterValue);
      },
      Cell: ({ row }) => {
        if (row.original.proximoControl === null) {
          return "";
        }
        const diff = moment(row.original.proximoControl).diff(
          Date.now(),
          "days",
        );
        return (
          <Chip
            variant="outlined"
            label={moment(row.original.proximoControl).format("DD/MM/YYYY")}
            sx={
              diff < 0
                ? StyledAccidentArtTable.errorChip
                : StyledAccidentArtTable.successChip
            }
          />
        );
      },
    },
  ], []);

  const formatTableDataForExport = (table) => {
    const tableDataWithFormat = table.map((fila) => [
      fila.art ? `"${fila.art}"` : "",
      fila.siniestro ? `"${fila.siniestro}"` : "",
      fila.documento ? `"${fila.documento}"` : "",
      fila.nombreTrabajador || fila.apellidoTrabajador
        ? (fila.nombreTrabajador !== "---" ? fila.nombreTrabajador : "") +
        (fila.apellidoTrabajador !== "---" ? fila.apellidoTrabajador : "")
        : "",
      fila.empleador ? `"${fila.empleador}"` : "",
      moment(fila.fechaHoraAccidente).format("L") || "",
      moment(fila.proximoControl).format("L") || "",
      fila.administrador ? `"${fila.administrador}"` : "",
      fila.cuitEmpleador ? `"${fila.cuitEmpleador}"` : "",
      fila.fechaAlta ? `"${fila.fechaAlta}"` : "",
      fila.fechaPrimeraCuracion
        ? moment(fila.fechaPrimeraCuracion).format("L")
        : "",
      fila.estado ? `"${fila.estado}"` : "",
    ]);

    return tableDataWithFormat;
  };

  const handleExportToCsv = () => {
    setIsDownloadingCsv(true);

    const columnsHeader = [
      "Art",
      "Siniestro",
      "Documento",
      "Trabajador",
      "Empleador",
      "Accidente",
      "Próximo Control",
      "Administrador",
      "CuitEmpleador",
      "Alta",
      "Primera Curación",
      "Estado",
    ];

    const formattedDataAsCsv = [
      columnsHeader,
      ...formatTableDataForExport(data),
    ];

    let csvContent = "sep=,\n";
    csvContent += `${formattedDataAsCsv.map((e) => e.join(",")).join("\n")}`;

    downloadCsv(csvContent, "Accidentes");
    snackbarUtils.success("Los siniestros han sido descargados con éxito.");
    setIsDownloadingCsv(false);
  };

  return (
    <>
      {displayAccidents !== "block" ? (
        <></>
      ) : (
        <MaterialReactTable
          {...tableConfig}
          defaultColumn={{
            minSize: 50,
            muiFilterTextFieldProps: {
              placeholder: '',
            },
            grow: false
          }}
          columns={columns}
          data={data}
          renderTopToolbarCustomActions={() => (
            <Box m={0.5} display="flex" justifyContent="space-evenly">
              <TitleContainer
                title="Siniestros"
                noRefresh={noRefresh}
                fetchData={fetchData}
              />

            </Box>
          )}
          positionActionsColumn="last"
          enableRowActions
          renderRowActions={({ row }) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "nowrap",
                gap: 0.5,
                whiteSpace: "nowrap",
              }}>
              {esPerfilDeSoporte && (
                <>
                  <Tooltip title={"Editar"}>
                    <IconButton size="small" onClick={() => handleEdit(row.original)}>
                      <EditOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={"Registros de edición"}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenLogsDialog(row.original)}>
                      <ErrorOutlineOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Ver Detalles">
                <IconButton
                  onClick={() => handleAccidentDetail(row.original)}
                  size="small"
                  color="inherit">
                  <Visibility />
                </IconButton>
              </Tooltip>
              {handleIngresarTraslado && (
                <Tooltip title="Ingresar nuevo traslado">
                  <IconButton
                    onClick={() => handleIngresarTraslado(row.original)}
                    size="small"
                    color="inherit">
                    <DirectionsCar />
                  </IconButton>
                </Tooltip>
              )}
              {handleVerTrasladosAnteriores && (
                <Tooltip title="Ver traslados anteriores">
                  <IconButton
                    onClick={() => handleVerTrasladosAnteriores(row.original)}
                    size="small"
                    color="inherit">
                    <Flag />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          state={{ isLoading: loading || isDownloadingCsv }}
          renderToolbarInternalActions={({ table }) => (
            <>
              <Tooltip title="Exportar CSV" arrow="top">
                <IconButton
                  color="inherit"
                  onClick={handleExportToCsv}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
              <MRT_ToggleFiltersButton table={table} />
              <MRT_ToggleDensePaddingButton table={table} />
              <MRT_ToggleFullScreenButton table={table} />
            </>
          )}

          renderDetailPanel={({ row }) => {
            return (
              <StyledAccidentArtTable.DetailsTableContainer>
                <DetailsTable rowData={row.original} />
              </StyledAccidentArtTable.DetailsTableContainer>
            );
          }}
        />
      )}
    </>
  );
};

export default AccidentArtTable;
